import { type SxProps, type Theme } from '@mui/material/styles'
import { densityPx, densityRem } from '@/theme/base/density'
import {
  SIDE_NAV_ITEM_FONT_SIZE,
  sideNavButtonHoverSx,
} from '@/layouts/dashboard/vertical-layout/side-nav-layout-dimensions'

export const COMMAND_BAR_TRIGGER_SHIMMER_DURATION_S = 4.4
const SHIMMER_KEYFRAMES = 'commandBarTriggerShimmer'
export const ICON_SLOT_SIZE_PX = densityPx(28)
const ICON_SIZE_PX = 16
const TEXT_GAP_PX = 8
const SHIMMER_BAND_PX = 64
/** Fin du balayage, comptée depuis la loupe : au-delà du raccourci, sidebar dépliée. */
const SHIMMER_SWEEP_END_PX = 300

/** Loupe de `SearchMd` (untitled-ui), en masque : `background-clip: text` ne découpe pas un SVG. */
const SEARCH_ICON_MASK = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21 21-4.35-4.35M19 11c0 4.4183-3.5817 8-8 8s-8-3.5817-8-8 3.5817-8 8-8 8 3.5817 8 8Z"/></svg>'
)}")`

/**
 * Text shimmer (TEC-1956). La loupe et le texte peignent chacun le même dégradé, positionné en px depuis la loupe
 * (`--shimmer-offset` = décalage de l'élément) : une seule bande traverse l'icône puis le texte. Une bande claire
 * passe sur une base atténuée, puis une pause avant le passage suivant.
 */
export const commandBarTriggerShimmerKeyframes = {
  [`@keyframes ${SHIMMER_KEYFRAMES}`]: {
    '0%': {
      backgroundPosition: `calc(-${SHIMMER_BAND_PX}px - var(--shimmer-offset)) 0, 0 0`,
    },
    '75%, 100%': {
      backgroundPosition: `calc(${SHIMMER_SWEEP_END_PX}px - var(--shimmer-offset)) 0, 0 0`,
    },
  },
}

const SHIMMER_BASE_COLOR =
  'color-mix(in srgb, var(--nav-item-color) 65%, transparent)'

const shimmerPaintSx = (offsetPx: number) => ({
  '--shimmer-offset': `${offsetPx}px`,
  animation: `${SHIMMER_KEYFRAMES} ${COMMAND_BAR_TRIGGER_SHIMMER_DURATION_S}s linear infinite`,
  backgroundImage: `linear-gradient(90deg, transparent, var(--nav-item-active-color), transparent), linear-gradient(${SHIMMER_BASE_COLOR}, ${SHIMMER_BASE_COLOR})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: `${SHIMMER_BAND_PX}px 100%, 100% 100%`,
})

export function getCommandBarTriggerButtonSx(
  isFolded: boolean
): SxProps<Theme> {
  return {
    ...commandBarTriggerShimmerKeyframes,
    alignItems: 'center',
    backgroundColor: 'var(--nav-item-hover-bg)',
    border: '1px solid var(--nav-border-color)',
    borderRadius: 1,
    display: 'flex',
    gap: isFolded ? 0 : `${TEXT_GAP_PX}px`,
    height: densityPx(40),
    justifyContent: 'flex-start',
    minWidth: 'unset',
    pl: `${densityPx(4)}px`,
    pr: `${densityPx(isFolded ? 16 : 6)}px`,
    textAlign: 'left',
    width: '100%',
    '&:hover': sideNavButtonHoverSx,
  }
}

export function getCommandBarTriggerIconSlotSx(): SxProps<Theme> {
  return {
    ...shimmerPaintSx(0),
    flex: '0 0 auto',
    height: ICON_SLOT_SIZE_PX,
    maskImage: SEARCH_ICON_MASK,
    maskPosition: 'center',
    maskRepeat: 'no-repeat',
    maskSize: `${ICON_SIZE_PX}px`,
    width: ICON_SLOT_SIZE_PX,
    WebkitMaskImage: SEARCH_ICON_MASK,
    WebkitMaskPosition: 'center',
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskSize: `${ICON_SIZE_PX}px`,
    '.MuiButtonBase-root:hover &': {
      animation: 'none',
      backgroundColor: 'var(--nav-item-active-color)',
      backgroundImage: 'none',
    },
    '@media (prefers-reduced-motion: reduce), (forced-colors: active)': {
      animation: 'none',
      backgroundColor: 'var(--nav-item-icon-color)',
      backgroundImage: 'none',
    },
  }
}

/** Libellé et raccourci partagent un dégradé découpé dans leur texte ; les enfants héritent du transparent. */
export function getCommandBarTriggerTextSx(): SxProps<Theme> {
  return {
    ...shimmerPaintSx(ICON_SLOT_SIZE_PX + TEXT_GAP_PX),
    alignItems: 'center',
    backgroundClip: 'text',
    color: 'transparent',
    display: 'flex',
    flexGrow: 1,
    gap: 1,
    minWidth: 0,
    WebkitBackgroundClip: 'text',
    '@media (prefers-reduced-motion: reduce), (forced-colors: active)': {
      animation: 'none',
      backgroundImage: 'none',
      color: 'var(--nav-item-color)',
    },
  }
}

export function getCommandBarTriggerLabelSx(showText: boolean): SxProps<Theme> {
  return {
    color: 'inherit',
    flexGrow: 1,
    fontSize: SIDE_NAV_ITEM_FONT_SIZE,
    fontWeight: 300,
    lineHeight: `${ICON_SLOT_SIZE_PX}px`,
    minWidth: 0,
    opacity: showText ? 1 : 0,
    overflow: 'hidden',
    pointerEvents: showText ? 'auto' : 'none',
    textOverflow: 'ellipsis',
    transform: showText ? 'translateX(0)' : `translateX(-${densityPx(10)}px)`,
    transition: 'opacity 0.3s ease, transform 0.3s ease',
    whiteSpace: 'nowrap',
  }
}

export function getCommandBarTriggerKeycapSx(): SxProps<Theme> {
  return {
    alignItems: 'center',
    border: '1px solid',
    borderColor: 'var(--nav-border-color)',
    borderRadius: 0.75,
    color: 'inherit',
    display: 'inline-flex',
    flexShrink: 0,
    fontFamily: 'inherit',
    fontSize: densityRem(0.65),
    fontWeight: 600,
    height: densityPx(20),
    justifyContent: 'center',
    lineHeight: 1,
    minWidth: densityPx(22),
    px: 0.75,
  }
}
